/**
 * What the support screens read from hg-content. Every key starts with
 * `SUPPORT_KEY`, so a new message refreshes all of it in one call.
 *
 * Every staff role reads and answers every thread.
 */
import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";

import { contentApi, orderApi, unwrap } from "@/shared/api/clients";
import { useLiveQuery } from "@/shared/live";

import type { Side } from "./types";

export const SUPPORT_KEY = ["support"] as const;

/** Rows of a list page. */
export const PER_PAGE = 25;
/** Messages read at a time, the newest first. */
export const MESSAGES = 30;

/** The filters `GET /v2/admin/support/threads` has. */
export interface ThreadsFilter {
  state?: "needs_reply" | "open" | "resolved";
  /** Only the threads of this app's side; left out: both. */
  side?: Side;
  q?: string;
  user_id?: string;
}

export function threadsQuery(filter: ThreadsFilter, page: number, perPage = PER_PAGE) {
  return queryOptions({
    queryKey: [...SUPPORT_KEY, "threads", filter, page, perPage] as const,
    queryFn: ({ signal }) =>
      unwrap(
        contentApi.GET("/v2/admin/support/threads", {
          params: { query: { ...filter, page, per_page: perPage } },
          signal,
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/** How many threads stand where: the badge, the tabs, the first screen's number. */
export const countsQuery = queryOptions({
  queryKey: [...SUPPORT_KEY, "counts"] as const,
  queryFn: async ({ signal }) => {
    const { data } = await unwrap(contentApi.GET("/v2/admin/support/threads/counts", { signal }));
    return data;
  },
});

/** How many threads are owed a reply: the rail's number. Undefined until known. */
export function useSupportWaitingCount(): number | undefined {
  return useLiveQuery({ ...countsQuery, select: (counts) => counts.needs_reply }).data;
}

export function threadKey(threadId: string) {
  return [...SUPPORT_KEY, "thread", threadId] as const;
}

export function threadQuery(threadId: string) {
  return queryOptions({
    queryKey: threadKey(threadId),
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        contentApi.GET("/v2/admin/support/threads/{thread_id}", {
          params: { path: { thread_id: threadId } },
          signal,
        }),
      );
      return data;
    },
  });
}

/** A thread's messages, the newest first; the older ones a slice at a time. */
export function messagesQuery(threadId: string) {
  return infiniteQueryOptions({
    queryKey: [...threadKey(threadId), "messages"] as const,
    queryFn: ({ pageParam, signal }) =>
      unwrap(
        contentApi.GET("/v2/admin/support/threads/{thread_id}/messages", {
          params: {
            path: { thread_id: threadId },
            query: { limit: MESSAGES, ...(pageParam === null ? {} : { cursor: pageParam }) },
          },
          signal,
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) =>
      last.pagination.has_more ? (last.pagination.next_cursor ?? null) : null,
  });
}

/**
 * The thread a specialist has in the specialist app, if any: how their page
 * finds it. Somebody who is a client too has another thread in the client
 * app, which is not the one a message from this page goes into.
 */
export function threadOfQuery(userId: string) {
  return queryOptions({
    queryKey: [...SUPPORT_KEY, "of", userId] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        contentApi.GET("/v2/admin/support/threads", {
          params: { query: { user_id: userId, side: "specialist", page: 1, per_page: 1 } },
          signal,
        }),
      );
      return data[0] ?? null;
    },
  });
}

/** A specialist's name and number, from hg-order: whom a first message is written to. */
export function specialistQuery(userId: string) {
  return queryOptions({
    queryKey: [...SUPPORT_KEY, "specialist", userId] as const,
    queryFn: async ({ signal }) => {
      const { data } = await unwrap(
        orderApi.GET("/v2/admin/specialists/{user_id}", {
          params: { path: { user_id: userId } },
          signal,
        }),
      );
      return { name: data.full_name ?? null, displayId: data.display_id };
    },
  });
}
